import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { ICON_TYPES } from "@shared/common/constants";
import Text from "common/Typography/Text";
import {
  ActiveTradeNextOrder,
  LanguageNameSpaces,
  OrderTypes,
  TradeData,
  TradeDataWithKey,
} from "@shared/types";
import NextOrder from "features/bots/MyBots/NextOrder";
import { typography } from "@shared/common/Themes/typography";
import PnlComponent from "features/dashboard/OpenTrades/PnlComponent";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Flex from "common/Boxes/Flex";
import React, { ReactNode, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import Tag from "common/Tags/Tag";
import { hexToRGB } from "@shared/utils/misc";
import TradeTimeInfo from "features/trades/TradesCards/TradeTimeInfo";
import { useTradeCardExtraInfo } from "@shared/features/trades/common/hooks";
import { AccountObjectType } from "@shared/features/settings/types";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import TextWithIcon from "common/Text/TextWithIcon";
import { SocketPnlResponse } from "@shared/services/socket/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import useNextOrder from "@shared/common/LiveComponents/useNextOrder";
import { formatDate } from "@shared/utils/date";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import useMoneyFormat, {
  formatSignedPercent,
  MINUS,
  shownPercent,
} from "features/trades/NewLook/useMoneyFormat.ts";

interface TradeCardExtraInfoProps {
  tradeData: TradeDataWithKey;
  isOpenTrades?: boolean;
  socketPnl?: SocketPnlResponse | undefined;
}

const TradeCardExtraInfo = ({
  tradeData,
  isOpenTrades,
  socketPnl,
}: TradeCardExtraInfoProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { theme, pricePrecision, orders, getTradeType } = useTradeCardExtraInfo(
    { data: tradeData, isOpenTrades }
  );

  const textStyle = useMemo(() => {
    return {
      fontSize: typography.fontSizes.M,
      color: theme.colors.primaryGrey,
    };
  }, [theme]);

  const getNextOrder = (orderType: OrderTypes) => {
    const name =
      orderType === OrderTypes.TAKE_PROFIT
        ? t("table.headers.next_take_profit")
        : t("table.headers.next_entry_or_stop");
    return {
      name,
      value: (
        <NextOrder
          nextOrder={orders[orderType]}
          symbol={tradeData.symbol}
          exchange={tradeData.exchange}
          orderType={orderType}
          textStyle={textStyle}
        />
      ),
    };
  };

  const extraInfoData = useMemo(() => {
    const extraInfoData = [
      {
        name: t("card.average_entry_price"),
        value: tradeData.entry_price ? (
          <TextWithPrecisionAndIcon
            iconType={ICON_TYPES.COIN}
            iconSize="m"
            coinOrExchangeName={tradeData.symbol.split("/")[1]}
            text={tradeData.entry_price}
            precision={pricePrecision}
            textStyle={{ color: theme.colors.primaryGrey }}
          />
        ) : (
          <Text>{t("card.not_available")}</Text>
        ),
      },
      {
        name: t("table.headers.pnl"),
        value: (
          <PnlComponent
            tradeData={tradeData}
            fontSize={typography.pxFontSizes.M}
            changeFontSize={typography.pxFontSizes.S}
            socketPnl={socketPnl}
          />
        ),
      },
    ];
    if (isOpenTrades) {
      extraInfoData.push(
        getNextOrder(tradeData.next_entry ? OrderTypes.ENTRY : OrderTypes.STOP),
        getNextOrder(OrderTypes.TAKE_PROFIT)
      );
    } else {
      extraInfoData.push(
        {
          name: t("table.headers.closed_at"),
          value: (
            <TradeTimeInfo
              timestamp={tradeData.close_timestamp}
              isOpen={false}
            />
          ),
        },
        {
          name: t("card.close_reason"),
          value: (
            <Text>{tradeData.close_reason || t("card.not_available")}</Text>
          ),
        }
      );
    }
    return extraInfoData;
  }, [tradeData, pricePrecision, theme, t, isOpenTrades, socketPnl]);

  const extraInfo = useMemo(
    () => (
      <Row gutter={[12, 12]}>
        {extraInfoData.map((item, index) => {
          return (
            <Col span={12} key={index}>
              <Flex $flexDirection="column" $alignItems="flex-start">
                <Text type="secondary" $size={typography.fontSizes.S}>
                  {item.name}
                </Text>
                {item.value}
              </Flex>
            </Col>
          );
        })}
      </Row>
    ),
    [extraInfoData]
  );

  const tradeDetails = useMemo(
    () => (
      <Row gutter={[12, 12]}>
        <Col span={12}>
          <InactiveAccountIcon
            objectType={AccountObjectType.ACCOUNT}
            accountId={tradeData.client_id}
            hideIcon={true}
            style={{ width: "100%" }}
            placement="topLeft"
          >
            <TextWithIcon
              iconType={ICON_TYPES.EXCHANGE}
              coinOrExchangeName={tradeData.exchange}
              text={tradeData.account}
              style={{ maxWidth: "100%", paddingRight: 4 }}
            />
          </InactiveAccountIcon>
        </Col>
        <Col span={12}>
          <Tag
            color={theme.colors.primaryGrey}
            bgColor={hexToRGB(theme.colors.secondaryGrey, 0.1)}
            style={{ maxWidth: "100%" }}
          >
            <Text
              style={{ fontWeight: "inherit", fontSize: "inherit" }}
              $ellipsis={true}
            >
              {tradeData.group}
            </Text>
          </Tag>
        </Col>
        <Col span={12}>
          <TradeTimeInfo timestamp={tradeData.open_timestamp} isOpen={true} />
        </Col>
        <Col span={12}>
          <Text>{getTradeType()}</Text>
        </Col>
      </Row>
    ),
    [tradeData, theme]
  );

  if (theme.isNewLook) {
    return (
      <NewLookTradeCardDetails
        tradeData={tradeData}
        isOpenTrades={isOpenTrades}
        socketPnl={socketPnl}
        pricePrecision={pricePrecision}
        tradeType={getTradeType()}
        orders={orders}
      />
    );
  }

  return (
    <>
      {extraInfo}
      <Divider />
      {tradeDetails}
    </>
  );
};

export default React.memo(TradeCardExtraInfo);

interface NewLookTradeCardDetailsProps {
  tradeData: TradeDataWithKey;
  isOpenTrades?: boolean;
  socketPnl?: SocketPnlResponse;
  pricePrecision: number;
  tradeType: string;
  orders: Record<OrderTypes, ActiveTradeNextOrder | undefined>;
}

/**
 * New look: the expanded row's details as one label-over-value grid, the
 * figures in the dashboard's roles (no tinted PnL pill, no source chip).
 */
const NewLookTradeCardDetails = ({
  tradeData,
  isOpenTrades,
  socketPnl,
  pricePrecision,
  tradeType,
  orders,
}: NewLookTradeCardDetailsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const { signedMoney, shown, isBtc } = useMoneyFormat();
  const live = socketPnl as Partial<TradeData> | undefined;
  const pnl =
    Number(
      (live || tradeData)?.[isBtc ? "btc_amount_change" : "usd_amount_change"]
    ) || 0;
  const graphData = (live || tradeData)?.graph_data;
  const percent =
    graphData && "profit" in graphData ? Number(graphData.profit) : undefined;
  const quote = tradeData.symbol.split("/")[1];

  const nextOrder = (orderType: OrderTypes) => (
    <NewLookNextOrder
      trade={tradeData}
      nextOrder={orders[orderType]}
      orderType={orderType}
    />
  );

  const items: { key: string; label: ReactNode; value: ReactNode }[] = [
    {
      key: "entry",
      label: t("card.average_entry_price"),
      value: tradeData.entry_price ? (
        <TextWithPrecisionAndIcon
          iconType={ICON_TYPES.COIN}
          iconSize="s"
          coinOrExchangeName={quote}
          text={tradeData.entry_price}
          precision={pricePrecision}
        />
      ) : (
        t("card.not_available")
      ),
    },
    {
      key: "pnl",
      label: t("table.headers.pnl"),
      value: (
        <span style={{ color: directionColor(tokens, shown(pnl)) }}>
          {signedMoney(pnl)}
          {percent !== undefined && (
            <DetailMeta
              style={{ color: directionColor(tokens, shownPercent(percent)) }}
            >
              {formatSignedPercent(percent)}
            </DetailMeta>
          )}
        </span>
      ),
    },
  ];
  if (isOpenTrades) {
    items.push(
      {
        key: "next-entry",
        label: t("table.headers.next_entry_or_stop"),
        value: nextOrder(
          tradeData.next_entry ? OrderTypes.ENTRY : OrderTypes.STOP
        ),
      },
      {
        key: "next-tp",
        label: t("table.headers.next_take_profit"),
        value: nextOrder(OrderTypes.TAKE_PROFIT),
      }
    );
  } else {
    items.push(
      {
        key: "closed-at",
        label: t("table.headers.closed_at"),
        value: formatDate(tradeData.close_timestamp, true, true),
      },
      {
        key: "close-reason",
        label: t("card.close_reason"),
        value: tradeData.close_reason || t("card.not_available"),
      }
    );
  }
  items.push(
    {
      key: "account",
      label: t("general_tab.fields.account"),
      value: (
        <InactiveAccountIcon
          objectType={AccountObjectType.ACCOUNT}
          accountId={tradeData.client_id}
          hideIcon={true}
          style={{ width: "100%" }}
          placement="topLeft"
        >
          <TextWithIcon
            iconType={ICON_TYPES.EXCHANGE}
            coinOrExchangeName={tradeData.exchange}
            text={tradeData.account}
            style={{ maxWidth: "100%" }}
          />
        </InactiveAccountIcon>
      ),
    },
    {
      key: "source",
      label: t("general_tab.fields.source"),
      value: tradeData.group,
    },
    {
      key: "opened-at",
      label: t("table.headers.opened_at"),
      value: formatDate(tradeData.open_timestamp, true, true),
    },
    {
      key: "type",
      label: t("general_tab.fields.trade_type"),
      value: tradeType,
    }
  );

  return (
    <DetailsGrid>
      {items.map((item) => (
        <Detail key={item.key}>
          <DetailLabel>{item.label}</DetailLabel>
          <DetailValue>{item.value}</DetailValue>
        </Detail>
      ))}
    </DetailsGrid>
  );
};

const NewLookNextOrder = ({
  trade,
  nextOrder,
  orderType,
}: {
  trade: TradeData;
  nextOrder: ActiveTradeNextOrder | undefined;
  orderType: OrderTypes;
}) => {
  const tokens = getNewLookTokens(useTheme());
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { pair, priceText, pricePrecision, distanceText } = useNextOrder({
    symbol: trade.symbol,
    exchange: trade.exchange,
    nextOrder,
    orderType,
  });
  if (!nextOrder) return <>{t("card.not_available")}</>;
  return (
    <span>
      <TextWithPrecisionAndIcon
        iconType={ICON_TYPES.COIN}
        iconSize="s"
        coinOrExchangeName={pair}
        text={priceText}
        precision={pricePrecision}
      />
      <DetailMeta
        style={orderType === OrderTypes.STOP ? { color: tokens.stop } : {}}
      >
        {distanceText.replace(/^-/, MINUS)}
      </DetailMeta>
    </span>
  );
};

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
`;
const Detail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const DetailLabel = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const DetailValue = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  .ant-typography {
    font-size: inherit !important;
    font-weight: inherit !important;
    color: inherit !important;
  }
`;
const DetailMeta = styled.span`
  margin-left: 6px;
  ${newLookType.meta};
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Divider = styled.div`
  border-top: ${(p) => p.theme.borders.lightest};
  width: 100%;
  margin-top: 12px;
  padding-bottom: 12px;
`;
